import React, { FC, useState } from "react";
import { Button, Form, message, Spin } from "antd";
import FormInput from "../../../components/FormInput/FormInput";
import { BASE_URL } from "../../../constants/urlConstants";

const AddPincode: FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);

  /* ================= PINCODE → CITY / STATE (POST OFFICE API) ================= */
  const fetchCityState = async (pincode: string) => {
    if (!/^\d{6}$/.test(pincode)) return;

    setFetchingLocation(true);
    form.setFieldsValue({ city: "", state: "" });

    try {
      const res = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      const data = await res.json();

      if (!data?.[0]?.PostOffice?.length) {
        message.error("Invalid pincode");
        return;
      }

      const po = data[0].PostOffice[0];

      form.setFieldsValue({
        city: po.District,
        state: po.State,
      });
    } catch {
      message.error("Unable to fetch city/state");
    } finally {
      setFetchingLocation(false);
    }
  };

  /* ================= SUBMIT ================= */
  const onFinish = async (values: any) => {
    try {
      if (!values.city || !values.state) {
        message.error("City and State not resolved");
        return;
      }

      setLoading(true);

      const res = await fetch(`${BASE_URL}/api/v1/order/pincode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pincode: values.pincode,
          city: values.city,
          state: values.state,
          isActive: true,
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to add pincode");
      }

      message.success("Pincode added successfully");
      form.resetFields();
    } catch (err: any) {
      message.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      style={{ maxWidth: 420 }}
      onValuesChange={(_, all) => {
        const pincode = all.pincode;

        if (!pincode || pincode.length < 6) {
          form.setFieldsValue({ city: "", state: "" });
          return;
        }

        if (pincode.length === 6) {
          fetchCityState(pincode);
        }
      }}
    >
      {/* PINCODE */}
      <Form.Item
        name="pincode"
        label="Pincode"
        rules={[
          { required: true, message: "Pincode is required" },
          { len: 6, message: "Pincode must be 6 digits" },
        ]}
      >
        <input
          className="ant-input"
          maxLength={6}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "");
          }}
        />
      </Form.Item>

      {/* CITY */}
      <FormInput
        title="City"
        name="city"
        titleSpan={8}
        wrapperSpan={16}
        disabled
        placeholder="Auto-filled from pincode"
      />

      {/* STATE */}
      <FormInput
        title="State"
        name="state"
        titleSpan={8}
        wrapperSpan={16}
        disabled
        placeholder="Auto-filled from pincode"
      />

      <Button
        type="primary"
        htmlType="submit"
        loading={loading}
        block
        disabled={fetchingLocation}
      >
        {fetchingLocation ? <Spin /> : "Add Pincode"}
      </Button>
    </Form>
  );
};

export default AddPincode;
